// src/QRmessaging/QRMessageList.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './QRclient.css';
import { QRCodeSVG } from 'qrcode.react';

const QRMessageList = ({ language, user, SERVER_ROOT }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'sent', 'received'

  const [qrCodeData, setQRCodeData] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [qrLoading, setQRLoading] = useState(false);
  
  
  // 메시지 목록 로딩
  useEffect(() => {
    if (!user || !user.email) {
      setError(language === 'KR' ? '메시지 목록을 보려면 로그인이 필요합니다.' : 'Please log in to view your messages.');
      setLoading(false);
      return;
    }
    
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${SERVER_ROOT}/api/qr/messages/user`, {
          params: {
            email: user.email,
            type: filter !== 'all' ? filter : undefined
          }
        });
        
        setMessages(response.data.messages || []);
      } catch (error) {
        console.error('메시지 목록 로딩 오류:', error);
        setError(language === 'KR' ? '메시지 목록을 불러오는 중 오류가 발생했습니다.' : 'Error loading messages.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, [SERVER_ROOT, language, user, filter]);
  
  // 메시지 열기 처리
  const handleOpenMessage = (messageId) => {
    navigate(`/qr/view?id=${messageId}`);
  };
  
  // 메시지 삭제 처리 (선택적 기능)
  const handleDeleteMessage = async (messageId, e) => {
    e.stopPropagation(); // 부모 요소의 클릭 이벤트 방지
    
    if (!window.confirm(language === 'KR' ? '이 메시지를 삭제하시겠습니까?' : 'Are you sure you want to delete this message?')) {
      return;
    }
    
    try {
      await axios.delete(`${SERVER_ROOT}/api/qr/message/${messageId}`, {
        params: { email: user.email }
      });
      
      // 메시지 목록에서 삭제된 메시지 제거
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      console.error('메시지 삭제 오류:', error);
      alert(language === 'KR' ? '메시지 삭제 중 오류가 발생했습니다.' : 'Error deleting message.');
    }
  };
  
  // 날짜 포맷 헬퍼 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // 로딩 화면
  if (loading) {
    return (
      <div className="qr-message-list">
        <div className="qr-loading">
          <div className="qr-spinner"></div>
          <p>{language === 'KR' ? '메시지 로딩 중...' : 'Loading messages...'}</p>
        </div>
      </div>
    );
  }
  
  // 오류 화면
  if (error) {
    return (
      <div className="qr-message-list">
        <div className="qr-error">
          <p>{error}</p>
          {!user || !user.email ? (
            <button
              onClick={() => navigate('/')}
              className="qr-btn"
            >
              {language === 'KR' ? '로그인 페이지로 이동' : 'Go to Login Page'}
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="qr-btn"
            >
              {language === 'KR' ? '다시 시도' : 'Try Again'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // QR 코드 보기 함수 추가
  const handleViewQRCode = async (messageId) => {
    try {
      setQRLoading(true);
      
      // 메시지 URL 생성
      const baseUrl = window.location.origin;
      const messageUrl = `${baseUrl}/qr/view?id=${messageId}`;
      
      // QR 코드 모달 표시
      setQRCodeData({
        url: messageUrl,
        messageId
      });
      setShowQRModal(true);
    } catch (error) {
      console.error('QR 코드 생성 오류:', error);
      alert(language === 'KR' ? 'QR 코드 생성 중 오류가 발생했습니다.' : 'Error generating QR code.');
    } finally {
      setQRLoading(false);
    }
  };

  // 메시지 공유 함수 추가
  const handleShareMessage = (messageId) => {
    // 공유 옵션 모달 표시
    setSelectedMessageId(messageId);
    setShowShareModal(true);
  };

  // QRMessageList.js에 QR 코드 모달 컴포넌트 추가
  const QRCodeModal = ({ show, onClose, qrData, language }) => {
    if (!show) return null;
    
    return (
      <div className="qr-modal">
        <div className="qr-modal-content">
          <div className="qr-modal-header">
            <h3>{language === 'KR' ? 'QR 코드' : 'QR Code'}</h3>
            <button className="qr-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="qr-modal-body">
            <div className="qr-code-container">
              <QRCodeSVG
                value={qrData.url}
                size={250}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="qr-modal-url">{qrData.url}</p>
          </div>
          <div className="qr-modal-footer">
            <button
              className="qr-btn"
              onClick={() => {
                // QR 코드 이미지 다운로드
                const svg = document.querySelector('.qr-code-container svg');
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  canvas.width = img.width;
                  canvas.height = img.height;
                  ctx.drawImage(img, 0, 0);
                  const pngFile = canvas.toDataURL('image/png');
                  const downloadLink = document.createElement('a');
                  downloadLink.download = `qr-message-${qrData.messageId}.png`;
                  downloadLink.href = pngFile;
                  downloadLink.click();
                };
                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
              }}
            >
              {language === 'KR' ? 'QR 코드 저장' : 'Save QR Code'}
            </button>
            <button
              className="qr-btn qr-btn-secondary"
              onClick={() => {
                navigator.clipboard.writeText(qrData.url);
                alert(language === 'KR' ? 'URL이 클립보드에 복사되었습니다!' : 'URL copied to clipboard!');
              }}
            >
              {language === 'KR' ? 'URL 복사' : 'Copy URL'}
            </button>
          </div>
        </div>
      </div>
    );
  };
 
  // 간소화된 ShareModal 컴포넌트
  const ShareModal = ({ show, onClose, messageId, language }) => {
    const [copySuccess, setCopySuccess] = useState(false);
    
    if (!show) return null;
    
    // 메시지 URL 생성
    const baseUrl = window.location.origin;
    const messageUrl = `${baseUrl}/qr/view?id=${messageId}`;
    
    // URL 복사 함수
    const copyToClipboard = () => {
      navigator.clipboard.writeText(messageUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    };
    
    // 소셜 미디어 공유 함수
    const shareToSocial = () => {
      if (navigator.share) {
        navigator.share({
          title: language === 'KR' ? 'QR 코드 메시지' : 'QR Code Message',
          text: language === 'KR' ? 'QR 코드 메시지를 확인해보세요!' : 'Check out this QR code message!',
          url: messageUrl
        }).catch(err => {
          console.error('공유 실패:', err);
        });
      } else {
        copyToClipboard();
      }
    };
    
    return (
      <div className="qr-modal">
        <div className="qr-modal-content">
          <div className="qr-modal-header">
            <h3>{language === 'KR' ? '메시지 공유' : 'Share Message'}</h3>
            <button className="qr-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="qr-modal-body">
            <div className="qr-share-content">
              <p className="qr-share-instruction">
                {language === 'KR' 
                  ? '이 메시지를 공유하는 방법은 다음과 같습니다:' 
                  : 'Here are ways to share this message:'}
              </p>
              
              <div className="qr-code-container">
                <QRCodeSVG
                  value={messageUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              <div className="qr-share-url-container">
                <input
                  type="text"
                  value={messageUrl}
                  readOnly
                  className="qr-share-url"
                  onClick={(e) => e.target.select()}
                />
                <button 
                  className="qr-btn-copy"
                  onClick={copyToClipboard}
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
              
              {copySuccess && (
                <p className="qr-success-message">
                  {language === 'KR' ? 'URL이 클립보드에 복사되었습니다!' : 'URL copied to clipboard!'}
                </p>
              )}
            </div>
          </div>
          <div className="qr-modal-footer">
            <button
              className="qr-btn"
              onClick={() => {
                // QR 코드 이미지 다운로드
                const svg = document.querySelector('.qr-code-container svg');
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  canvas.width = img.width;
                  canvas.height = img.height;
                  ctx.drawImage(img, 0, 0);
                  const pngFile = canvas.toDataURL('image/png');
                  const downloadLink = document.createElement('a');
                  downloadLink.download = `qr-message-${messageId}.png`;
                  downloadLink.href = pngFile;
                  downloadLink.click();
                };
                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
              }}
            >
              {language === 'KR' ? 'QR 코드 저장' : 'Save QR Code'}
            </button>
            
            {navigator.share && (
              <button
                className="qr-btn qr-btn-secondary"
                onClick={shareToSocial}
              >
                {language === 'KR' ? '공유하기' : 'Share'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="qr-message-list">
      <div className="qr-message-filters">
        <button
          className={`qr-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {language === 'KR' ? '모든 메시지' : 'All Messages'}
        </button>
        <button
          className={`qr-filter-btn ${filter === 'sent' ? 'active' : ''}`}
          onClick={() => setFilter('sent')}
        >
          {language === 'KR' ? '보낸 메시지' : 'Sent Messages'}
        </button>
        <button
          className={`qr-filter-btn ${filter === 'received' ? 'active' : ''}`}
          onClick={() => setFilter('received')}
        >
          {language === 'KR' ? '받은 메시지' : 'Received Messages'}
        </button>
      </div>
      
      {/* QR 코드 모달 */}
      <QRCodeModal
        show={showQRModal}
        onClose={() => setShowQRModal(false)}
        qrData={qrCodeData}
        language={language}
      />
      
      {/* 공유 모달 */}
      <ShareModal
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        messageId={selectedMessageId}
        language={language}
        SERVER_ROOT={SERVER_ROOT}
        user={user}
      />
      
      {messages.length === 0 ? (
        <div className="qr-no-messages">
          <p>
            {filter === 'sent' 
              ? (language === 'KR' ? '보낸 메시지가 없습니다.' : 'No sent messages.') 
              : filter === 'received' 
                ? (language === 'KR' ? '받은 메시지가 없습니다.' : 'No received messages.') 
                : (language === 'KR' ? '메시지가 없습니다.' : 'No messages.')}
          </p>
        </div>
      ) : (
        <div className="qr-message-table-container">
          <table className="qr-message-table">
            <thead>
              <tr>
                <th>{language === 'KR' ? '유형' : 'Type'}</th>
                <th>{language === 'KR' ? '내용 미리보기' : 'Preview'}</th>
                <th>{filter !== 'sent' ? (language === 'KR' ? '보낸 사람' : 'From') : (language === 'KR' ? '받는 사람' : 'To')}</th>
                <th>{language === 'KR' ? '날짜' : 'Date'}</th>
                <th>{language === 'KR' ? '상태' : 'Status'}</th>
                <th>{language === 'KR' ? '작업' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(message => (
                <tr 
                  key={message.id}
                  onClick={() => handleOpenMessage(message.id)}
                  className={`qr-message-row ${!message.read_at && message.receiver_email === user.email ? 'unread' : ''}`}
                >
                  <td>
                    {message.message_type === 'text' ? (language === 'KR' ? '텍스트' : 'Text') : 
                     message.message_type === 'url' ? (language === 'KR' ? 'URL' : 'URL') :
                     message.message_type === 'vcard' ? (language === 'KR' ? '명함' : 'Contact') :
                     (language === 'KR' ? '기타' : 'Other')}
                  </td>
                  <td className="qr-message-preview">
                    {message.message_type === 'text' ? 
                      (message.message.length > 30 ? message.message.substring(0, 30) + '...' : message.message) :
                     message.message_type === 'url' ? 
                      (message.message.length > 30 ? message.message.substring(0, 30) + '...' : message.message) :
                     message.message_type === 'vcard' ? 
                      (language === 'KR' ? '연락처 정보' : 'Contact Information') :
                      (language === 'KR' ? '내용 보기' : 'View Content')}
                  </td>
                  <td>
                    {filter !== 'sent' ? message.sender_email : message.receiver_email}
                  </td>
                  <td>{formatDate(message.created_at)}</td>
                  <td>
                    {message.read_at ? 
                      (language === 'KR' ? '읽음' : 'Read') : 
                      (language === 'KR' ? '안 읽음' : 'Unread')}
                  </td>
                  <td>
                    <button
                      className="qr-btn-icon qr-btn-delete"
                      onClick={(e) => handleDeleteMessage(message.id, e)}
                      title={language === 'KR' ? '삭제' : 'Delete'}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                  <td className="qr-message-actions">
                    <button
                      className="qr-btn-icon qr-btn-view"
                      onClick={(e) => {
                        e.stopPropagation(); // 부모 요소의 클릭 방지
                        handleViewQRCode(message.id);
                      }}
                      title={language === 'KR' ? 'QR코드 보기' : 'View QR Code'}
                    >
                      <i className="fas fa-qrcode"></i>
                    </button>
                    
                    <button
                      className="qr-btn-icon qr-btn-share"
                      onClick={(e) => {
                        e.stopPropagation(); // 부모 요소의 클릭 방지
                        handleShareMessage(message.id);
                      }}
                      title={language === 'KR' ? '공유하기' : 'Share'}
                    >
                      <i className="fas fa-share-alt"></i>
                    </button>
                    
                    <button
                      className="qr-btn-icon qr-btn-delete"
                      onClick={(e) => handleDeleteMessage(message.id, e)}
                      title={language === 'KR' ? '삭제' : 'Delete'}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QRMessageList;